import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useCallback, useState, useEffect, useRef, } from 'react';
import { authErrorMessage, clearAuthErrorDiagnostics, getAuthDiagnostic, persistAuthFailure } from '../auth/authDiagnostics';
import { useNavigate, useLocation } from 'react-router-dom';
import { initAppKit, getAppKitSync, getWalletProvider, setInjectedWalletProvider, projectId, signMessage as wcSignMessage, testProviderHealth, forceReconnect, getConnectedAddress, hasExistingWcPairing } from './mobileWalletConfig';
import { shouldUseInjectedMetaMaskPreview, } from '../../utils/replitMobilePreview';
import { fullLoginProcess, loadJwt, loadRefreshToken, loadWalletAddress, clearAllMobileAuthData, storeWalletAddress, hasCachedMpcData, generateExpirationTimeStamp, setPreSignedForEndpoint, buildEndpointMessage, clearPreSignedCache, } from '../auth/mobileMpcService';
import { attemptRefreshToken, validateStoredAccessToken, invalidateRefreshBinding, isRefreshBindingCurrent, isAuthInvalidationCurrent, markSessionCreated, clearSessionCreated, isJwtExpired, } from '../auth/authRefreshManager';
import { isGuestRoute, isGuestPath } from '../../utils/guestRoute';
import { hasSavedSymmetricKey, clearKeySignature, getSymmetricKeyChallenge, storeKeySignature } from '../auth/mobileEncryption';
import { checkWalletWhitelist, hasProtectedWalletAccess, useBackendAuthStatus, } from '../../walletprovider/walletWhitelist';
import { createConnectionOperationGuard, createGuardedConnectionSession, StaleConnectionOperationError, transitionRestoredWalletAccount, shouldClearWalletScopedDataAfterBackendFailure, shouldRecoverStaleConnectLock, EXPLICIT_PAIRING_RESTORE_ATTEMPTS, } from './connectionOperationGuard';
const MobileWalletContext = createContext({
    address: undefined,
    walletProvider: null,
    isConnected: false,
    isConnecting: false,
    isAuthenticated: false,
    isInitializing: true,
    isWaitingForWallet: false,
    connect: async () => { },
    cancelConnect: () => { },
    disconnect: () => { },
    error: null,
    isConfigured: false,
    jwt: null,
    connector: undefined,
    onboardingStatus: 'idle',
    onboardingError: null,
    startOnboarding: () => { },
    resumeOnboarding: () => { },
    hasInterruptedOnboarding: false,
    signatureCount: 0,
    waitingForSignatures: false,
    biometricState: 'idle',
    retryBiometric: () => { },
    needsManualSign: false,
    continueSignature: () => { },
    isReconnecting: false,
    walletSessionVersion: 0,
    backendAuthStatus: 'idle',
    authoritativeBackendDenial: false,
    requiresExplicitConfirmation: false,
    whitelistStatus: 'idle',
    whitelistUnavailableReason: null,
    retryWhitelistCheck: () => { },
});
const AUTH_PROGRESS_KEY = 'mobile_auth_in_progress';
const AUTH_PROGRESS_TTL_MS = 10 * 60 * 1000;
// Android installed PWAs can take substantially longer to rehydrate a
// WalletConnect transport after being resumed than an ordinary browser tab.
// This is deliberately longer than the former ten-second heuristic, while
// still providing an escape from a promise which never settles.
const APPKIT_STARTUP_SETTLE_TIMEOUT_MS = 60_000;
function saveAuthInProgress(walletAddress) {
    try {
        const data = JSON.stringify({
            walletAddress: walletAddress.toLowerCase(),
            timestamp: Date.now(),
        });
        sessionStorage.setItem(AUTH_PROGRESS_KEY, data);
        localStorage.setItem(AUTH_PROGRESS_KEY, data);
        localStorage.removeItem('mobile_background_at');
    }
    catch { }
}
function loadAuthInProgress() {
    try {
        const raw = sessionStorage.getItem(AUTH_PROGRESS_KEY) || localStorage.getItem(AUTH_PROGRESS_KEY);
        if (!raw)
            return null;
        const data = JSON.parse(raw);
        if (!data.walletAddress || !data.timestamp)
            return null;
        if (Date.now() - data.timestamp > AUTH_PROGRESS_TTL_MS) {
            sessionStorage.removeItem(AUTH_PROGRESS_KEY);
            localStorage.removeItem(AUTH_PROGRESS_KEY);
            return null;
        }
        return data;
    }
    catch {
        return null;
    }
}
function clearAuthInProgress() {
    try {
        sessionStorage.removeItem(AUTH_PROGRESS_KEY);
        localStorage.removeItem(AUTH_PROGRESS_KEY);
    }
    catch { }
}
function normalizeAppKitChainId(network) {
    const raw = network?.chainId
        ?? network?.id
        ?? network?.caipNetwork?.id
        ?? network?.caipNetworkId
        ?? network;
    if (raw === null || raw === undefined)
        return null;
    const value = String(raw);
    const chainId = value.includes(':') ? value.slice(value.lastIndexOf(':') + 1) : value;
    if (/^0x[0-9a-f]+$/i.test(chainId)) {
        return String(parseInt(chainId, 16));
    }
    return chainId || null;
}
export function useMobileWallet() {
    return useContext(MobileWalletContext);
}
export function MobileWalletProvider({ children }) {
    // Persisted JWT/address data is not proof of a current wallet connection.
    // Protected access remains locked until AppKit reports a live account.
    const [address, setAddress] = useState(undefined);
    const [walletProvider, setWalletProvider] = useState(null);
    const [isConnected, setIsConnected] = useState(false);
    const [isConnecting, setIsConnecting] = useState(false);
    const [sessionStorageChecked, setSessionStorageChecked] = useState(false);
    const [appKitReady, setAppKitReady] = useState(false);
    const [isReconnecting, setIsReconnecting] = useState(() => {
        if (isGuestRoute())
            return false;
        const pending = sessionStorage.getItem('mobile_connect_pending');
        if (!pending)
            return false;
        const elapsed = Date.now() - parseInt(pending, 10);
        return elapsed < 120000;
    });
    // Storage is only input to a signed backend refresh; it never authorizes a
    // route before a current live wallet has completed that backend flow.
    const [jwt, setJwt] = useState(null);
    const [backendAuthStatus, setBackendAuthStatus] = useState('idle');
    const [whitelistStatus, setWhitelistStatus] = useState('idle');
    const [whitelistUnavailableReason, setWhitelistUnavailableReason] = useState(null);
    const [whitelistRetry, setWhitelistRetry] = useState(0);
    const [authoritativeDenial, setAuthoritativeDenial] = useState(null);
    const [onboardingStatus, setOnboardingStatus] = useState('idle');
    const [onboardingError, setOnboardingError] = useState(null);
    const [signatureCount, setSignatureCount] = useState(0);
    const [waitingForSignatures, setWaitingForSignatures] = useState(false);
    const [interruptedOnboarding, setInterruptedOnboarding] = useState(false);
    const [error, setError] = useState(null);
    const [biometricState, setBiometricState] = useState('idle');
    const [needsManualSign, setNeedsManualSign] = useState(false);
    // A local JWT without a server-verifiable refresh credential cannot grant
    // access. Keep this separate from confirmed backend denial so the user may
    // deliberately begin one guarded signature flow.
    const [explicitConfirmation, setExplicitConfirmation] = useState(null);
    const [walletSessionVersion, setWalletSessionVersion] = useState(0);
    const [silentAuthRetryAttempt, setSilentAuthRetryAttempt] = useState(0);
    const navigate = useNavigate();
    const location = useLocation();
    const isAuthenticatingRef = useRef(false);
    const resumeScheduledRef = useRef(false);
    const biometricCheckStarted = useRef(false);
    const authTriggeredForAddress = useRef(null);
    const runAuthProcessRef = useRef(() => { });
    const connectingRef = useRef(false);
    const isConnectingRef = useRef(false);
    const connectAttemptRef = useRef(0);
    const lastConnectTime = useRef(0);
    const appKitSubscribed = useRef(false);
    const appKitChainIdRef = useRef(null);
    const appKitSubscriptionCleanupRef = useRef(null);
    const ignoredDisconnectedSnapshotAttemptRef = useRef(null);
    const transientWalletDisconnectRef = useRef(null);
    const connectHandoffRef = useRef(null);
    const injectedProviderCleanupRef = useRef(null);
    const reconnectInFlightRef = useRef(false);
    const connectionOperationGuardRef = useRef(createConnectionOperationGuard());
    const liveAddressRef = useRef(address);
    const liveConnectedRef = useRef(isConnected);
    const whitelistSessionRef = useRef({ address: address?.toLowerCase(), version: walletSessionVersion });
    const initSessionStarted = useRef(false);
    const guestInitSkippedRef = useRef(false);
    const jwtRef = useRef(jwt);
    const explicitOnboardingWalletRef = useRef(null);
    const lastWalletRef = useRef(loadWalletAddress()?.toLowerCase());
    jwtRef.current = jwt;
    liveAddressRef.current = address;
    liveConnectedRef.current = isConnected;
    whitelistSessionRef.current = { address: address?.toLowerCase(), version: walletSessionVersion };
    // This is a prompt requirement, never authorization or consent to sign.
    // Bind it to the same live wallet + generation as the guarded auth result.
    const setRequiresExplicitConfirmation = useCallback((required) => {
        setExplicitConfirmation(required && liveConnectedRef.current && liveAddressRef.current
            ? {
                walletAddress: liveAddressRef.current.toLowerCase(),
                operation: connectionOperationGuardRef.current.current(),
            }
            : null);
    }, []);
    const requiresExplicitConfirmation = isConnected
        && !!address
        && backendAuthStatus === 'idle'
        && whitelistStatus === 'authorized'
        && explicitConfirmation?.walletAddress === address.toLowerCase()
        && explicitConfirmation?.operation === connectionOperationGuardRef.current.current();
    const retryWhitelistCheck = useCallback(() => {
        setWhitelistRetry(attempt => attempt + 1);
    }, []);
    useEffect(() => {
        const account = address;
        const sessionVersion = walletSessionVersion;
        if (!isConnected || !account) {
            setWhitelistStatus('idle');
            setWhitelistUnavailableReason(null);
            return;
        }
        let active = true;
        setWhitelistStatus('checking');
        setWhitelistUnavailableReason(null);
        void checkWalletWhitelist(account).then(result => {
            if (!active
                || liveAddressRef.current?.toLowerCase() !== account.toLowerCase()
                || !liveConnectedRef.current
                || whitelistSessionRef.current.version !== sessionVersion)
                return;
            setWhitelistStatus(result.status);
            setWhitelistUnavailableReason(result.status === 'unavailable' ? result.reason : null);
        });
        return () => { active = false; };
    }, [address, isConnected, walletSessionVersion, whitelistRetry]);
    const transitionConnectedAccount = useCallback((restoredAddress) => {
        const normalizedRestoredAddress = restoredAddress.toLowerCase();
        if (explicitOnboardingWalletRef.current === '__pending_connect__') {
            explicitOnboardingWalletRef.current = normalizedRestoredAddress;
        }
        else if (explicitOnboardingWalletRef.current !== normalizedRestoredAddress) {
            explicitOnboardingWalletRef.current = null;
        }
        const transition = transitionRestoredWalletAccount(connectionOperationGuardRef.current, { address: liveAddressRef.current, isConnected: liveConnectedRef.current }, restoredAddress, loadWalletAddress(), !!loadJwt() || hasCachedMpcData() || hasSavedSymmetricKey(), () => {
            clearAllMobileAuthData();
            invalidateRefreshBinding();
            clearKeySignature();
            clearSessionCreated();
            clearAuthInProgress();
            clearPreSignedCache();
            setJwt(null);
            setAuthoritativeDenial(null);
            setBackendAuthStatus('idle');
            setOnboardingStatus('idle');
            setOnboardingError(null);
            setInterruptedOnboarding(false);
            setRequiresExplicitConfirmation(false);
            explicitOnboardingWalletRef.current = null;
            authTriggeredForAddress.current = null;
        }, normalizedAddress => {
            // A connected transition is terminal for the connect generation.  In
            // addition to clearing the owned subscriptions, advance the generation
            // so already-queued AppKit close/state callbacks cannot turn this
            // successful transition back into a cancellation.
            ++connectAttemptRef.current;
            invalidateRefreshBinding();
            const handoff = connectHandoffRef.current;
            if (handoff?.timeout)
                clearTimeout(handoff.timeout);
            handoff?.cleanup?.();
            connectHandoffRef.current = null;
            isAuthenticatingRef.current = false;
            transientWalletDisconnectRef.current = null;
            authTriggeredForAddress.current = null;
            liveAddressRef.current = normalizedAddress;
            liveConnectedRef.current = true;
            setWalletProvider(getWalletProvider());
            lastWalletRef.current = normalizedAddress;
            setWhitelistStatus('checking');
            setWhitelistUnavailableReason(null);
            setBackendAuthStatus('checking');
            setAuthoritativeDenial(null);
            setError(null);
            setWalletSessionVersion(version => version + 1);
            setAddress(normalizedAddress);
            setIsConnected(true);
            connectingRef.current = false;
            isConnectingRef.current = false;
            setIsConnecting(false);
            sessionStorage.setItem('dwdata_wallet_connected', 'true');
        });
        return transition.address;
    }, []);
    const endConnectHandoff = useCallback((message, attempt) => {
        // AppKit can deliver close/cancel/state notifications after unsubscribe.
        // Only the generation that installed the callback may terminate the
        // current handoff. Calls without an attempt are intentional current
        // lifecycle operations (disconnect, explicit cancel, visibility timeout).
        if (attempt !== undefined && connectAttemptRef.current !== attempt)
            return;
        const handoff = connectHandoffRef.current;
        if (handoff?.timeout)
            clearTimeout(handoff.timeout);
        handoff?.cleanup?.();
        connectHandoffRef.current = null;
        explicitOnboardingWalletRef.current = null;
        ignoredDisconnectedSnapshotAttemptRef.current = null;
        connectionOperationGuardRef.current.invalidate();
        reconnectInFlightRef.current = false;
        connectingRef.current = false;
        isConnectingRef.current = false;
        setIsConnecting(false);
        setIsReconnecting(false);
        sessionStorage.removeItem('mobile_connect_pending');
        if (message)
            setError(new Error(message));
    }, []);
    const handleAccountChange = useCallback((account) => {
        const addr = account?.address;
        const connected = account?.isConnected ?? false;
        if (connected && addr) {
            // WalletConnect/AppKit may replay the currently active account while a
            // manual auth retry is refreshing the transport.  Treat that replay as
            // idempotent: transitionConnectedAccount invalidates the connection
            // generation, which would make the retry return silently before it can
            // reach fullLoginProcess.  A real account change still takes the
            // transition path below.
            if (liveConnectedRef.current
                && liveAddressRef.current?.toLowerCase() === addr.toLowerCase()) {
                return;
            }
            isAuthenticatingRef.current = false;
            transitionConnectedAccount(addr);
        }
        else {
            isAuthenticatingRef.current = false;
            // subscribeAccount replays its current state when installed. During a
            // hard refresh this is commonly a disconnected snapshot emitted before
            // WalletConnect has hydrated the saved pairing. It is not proof that the
            // user disconnected and must never erase persisted refresh credentials.
            if (!liveConnectedRef.current && account?.confirmed !== true) {
                console.log('[MobileAuth] Ignoring unconfirmed disconnected snapshot before live wallet restoration');
                return;
            }
            // AppKit subscriptions commonly emit an initial "disconnected" snapshot.
            // It is not a disconnect event and must not invalidate the tap which is
            // about to open the Connect modal.
            if (ignoredDisconnectedSnapshotAttemptRef.current === connectAttemptRef.current
                && connectingRef.current
                && !liveConnectedRef.current) {
                console.log('[MobileAuth] Ignoring initial disconnected snapshot during connect');
                return;
            }
            const walletHandoffActive = liveConnectedRef.current && !!liveAddressRef.current && (document.hidden ||
                connectingRef.current ||
                isAuthenticatingRef.current ||
                !!sessionStorage.getItem('mobile_connect_pending') ||
                !!loadAuthInProgress());
            if (walletHandoffActive && account?.confirmed !== true) {
                transientWalletDisconnectRef.current = {
                    wallet: liveAddressRef.current.toLowerCase(),
                    timestamp: Date.now(),
                };
                console.log('[MobileAuth] Deferring transient AppKit disconnect during wallet handoff');
                return;
            }
            transientWalletDisconnectRef.current = null;
            endConnectHandoff(connectingRef.current && !liveConnectedRef.current
                ? 'Wallet connection was cancelled. Please try again.'
                : undefined);
            liveAddressRef.current = undefined;
            liveConnectedRef.current = false;
            setWalletProvider(null);
            lastWalletRef.current = undefined;
            console.log('[MobileAuth] Wallet disconnected — invalidating authorization immediately');
            setIsReconnecting(false);
            reconnectInFlightRef.current = false;
            setAddress(undefined);
            setWalletProvider(null);
            setIsConnected(false);
            setWalletSessionVersion(version => version + 1);
            clearAllMobileAuthData();
            invalidateRefreshBinding();
            clearKeySignature();
            clearSessionCreated();
            clearAuthInProgress();
            clearPreSignedCache();
            setJwt(null);
            setAuthoritativeDenial(null);
            setWhitelistStatus('idle');
            setWhitelistUnavailableReason(null);
            setBackendAuthStatus('idle');
            setOnboardingStatus('idle');
            setOnboardingError(null);
            setInterruptedOnboarding(false);
            setRequiresExplicitConfirmation(false);
            explicitOnboardingWalletRef.current = null;
            authTriggeredForAddress.current = null;
        }
    }, [transitionConnectedAccount, endConnectHandoff]);
    const handleNetworkChange = useCallback((network) => {
        const chainId = normalizeAppKitChainId(network);
        if (!chainId)
            return;
        if (appKitChainIdRef.current === null) {
            appKitChainIdRef.current = chainId;
            return;
        }
        if (appKitChainIdRef.current === chainId)
            return;
        appKitChainIdRef.current = chainId;
        // A chain switch invalidates every wallet-scoped authorization, even when
        // the account address is unchanged. Invalidate pending auth work before
        // publishing the new session generation so stale completions cannot grant
        // access on the new chain.
        connectionOperationGuardRef.current.invalidate();
        ++connectAttemptRef.current;
        isAuthenticatingRef.current = false;
        authTriggeredForAddress.current = null;
        setBackendAuthStatus('checking');
        setWhitelistStatus('checking');
        setWhitelistUnavailableReason(null);
        setWalletSessionVersion(version => version + 1);
    }, []);
    const subscribeToAppKit = useCallback((modal) => {
        if (appKitSubscribed.current)
            return;
        appKitSubscribed.current = true;
        const cleanups = [];
        const accountCleanup = modal.subscribeAccount(handleAccountChange);
        if (typeof accountCleanup === 'function')
            cleanups.push(accountCleanup);
        if (typeof modal.subscribeNetwork === 'function') {
            appKitChainIdRef.current = null;
            const networkCleanup = modal.subscribeNetwork(handleNetworkChange);
            if (typeof networkCleanup === 'function')
                cleanups.push(networkCleanup);
        }
        appKitSubscriptionCleanupRef.current = () => {
            cleanups.splice(0).forEach(cleanup => cleanup());
            appKitSubscriptionCleanupRef.current = null;
            appKitSubscribed.current = false;
            appKitChainIdRef.current = null;
        };
    }, [handleAccountChange, handleNetworkChange]);
    const hasValidSession = !!jwt;
    const backendAuthForWallet = useBackendAuthStatus(address, isConnected, walletSessionVersion, backendAuthStatus);
    const isAuthenticated = hasValidSession &&
        hasProtectedWalletAccess(backendAuthForWallet, whitelistStatus);
    const isInitializing = !sessionStorageChecked;
    useEffect(() => {
        if (initSessionStarted.current)
            return;
        initSessionStarted.current = true;
        clearAuthErrorDiagnostics();
        if (isGuestRoute()) {
            // Guest (magic-link) screens: no wallet session, no AppKit,
            // no auto-reconnect — prevents MetaMask/WalletConnect popups.
            console.log('[MobileAuth] Guest route detected — skipping wallet session init');
            guestInitSkippedRef.current = true;
            setAppKitReady(true);
            return;
        }
        const initSession = async () => {
            const connectionOperation = connectionOperationGuardRef.current.begin();
            const isStaleConnectionOperation = () => !connectionOperationGuardRef.current.isCurrent(connectionOperation);
            const settleStartupAppKit = async (acceptLateModal) => {
                const appKitPromise = initAppKit();
                let timeout;
                const result = await Promise.race([
                    appKitPromise.then(modal => ({ modal, timedOut: false })),
                    new Promise(resolve => {
                        timeout = setTimeout(() => resolve({ modal: null, timedOut: true }), APPKIT_STARTUP_SETTLE_TIMEOUT_MS);
                    }),
                ]);
                if (timeout)
                    clearTimeout(timeout);
                if (!result.timedOut)
                    return result;
                // Do not clear wallet-scoped credentials: timeout is a transport/UI
                // failure, not proof that the saved wallet session is invalid. Keep
                // listening so a late result from this still-current generation can
                // restore the live account without authorizing from JWT storage alone.
                console.warn('[MobileAuth] AppKit startup did not settle; leaving restoration state');
                setAppKitReady(true);
                setBackendAuthStatus('unavailable');
                setOnboardingError('Wallet restoration is taking longer than expected. Please retry connecting to your wallet.');
                void appKitPromise.then(async (modal) => {
                    if (!modal || isStaleConnectionOperation())
                        return;
                    try {
                        await acceptLateModal(modal);
                    }
                    catch (e) {
                        console.warn('[MobileAuth] Late AppKit startup recovery failed:', getAuthDiagnostic(e));
                    }
                }).catch(e => {
                    if (!isStaleConnectionOperation()) {
                        console.warn('[MobileAuth] Late AppKit startup failed:', getAuthDiagnostic(e));
                    }
                });
                return result;
            };
            // Migrate the former tab-scoped acceptance marker once, then use the
            // authoritative persistent key everywhere.
            if (sessionStorage.getItem('mobile_terms_accepted') === 'true') {
                localStorage.setItem('dwinity_tc_accepted', 'true');
            }
            sessionStorage.removeItem('mobile_terms_accepted');
            // Persisted credentials are only considered after AppKit supplies the
            // live account; do not erase them while that restoration is pending.
            setAddress(undefined);
            setIsConnected(false);
            setOnboardingStatus('idle');
            const cachedJwtForSession = loadJwt();
            const hasValidCachedJwt = !!(cachedJwtForSession && !isJwtExpired(cachedJwtForSession));
            const hadPreviousSession = sessionStorage.getItem('dwdata_wallet_connected') === 'true' || !!(hasValidCachedJwt && loadWalletAddress());
            const hasPendingConnect = !!sessionStorage.getItem('mobile_connect_pending');
            const hasSessionJwt = hasValidCachedJwt;
            const hasCachedMpc = hasCachedMpcData();
            const hasWalletScopedRefresh = !!(loadRefreshToken() && loadWalletAddress());
            const pendingAuth = loadAuthInProgress();
            if (pendingAuth) {
                console.log('[MobileAuth] Auth in progress detected (localStorage) for', pendingAuth.walletAddress, '— preserving WC pairings for resume');
            }
            else if (!hasSessionJwt && !hasPendingConnect && !hasCachedMpc && !hasWalletScopedRefresh) {
                console.log('[MobileAuth] No active session and no cached MPC data — clearing orphaned WC pairings from localStorage');
                const wcPrefixes = ['wc@2:', 'W3M', '@w3m', 'wagmi.', '-walletlink', 'WALLETCONNECT', 'walletconnect', '@appkit'];
                const staleKeys = [];
                for (let i = 0; i < localStorage.length; i++) {
                    const key = localStorage.key(i);
                    if (key && wcPrefixes.some(p => key.startsWith(p))) {
                        staleKeys.push(key);
                    }
                }
                staleKeys.forEach(key => localStorage.removeItem(key));
                if (staleKeys.length > 0) {
                    console.log('[MobileAuth] Removed', staleKeys.length, 'orphaned WC pairing keys');
                }
            }
            else if (!hasSessionJwt && !hasPendingConnect && (hasCachedMpc || hasWalletScopedRefresh)) {
                console.log('[MobileAuth] Wallet-scoped refresh/MPC data found — preserving WC pairing for silent restore');
            }
            if (hadPreviousSession || hasPendingConnect || hasWalletScopedRefresh) {
                console.log('[MobileAuth] Previous session detected, lazy-loading AppKit...');
                try {
                    // A wallet-scoped refresh is a real restoration candidate. iOS may
                    // take longer than ten seconds to hydrate WalletConnect/AppKit after
                    // a hard refresh, so a heuristic timeout must not abandon the
                    // original promise and eject the protected route.
                    const { modal, timedOut } = await settleStartupAppKit(async (lateModal) => {
                        setAppKitReady(true);
                        subscribeToAppKit(lateModal);
                        await new Promise(r => setTimeout(r, 500));
                        if (isStaleConnectionOperation())
                            return;
                        const lateAddress = await getConnectedAddress();
                        if (!isStaleConnectionOperation() && lateAddress) {
                            handleAccountChange({ address: lateAddress, isConnected: true });
                        }
                    });
                    if (isStaleConnectionOperation())
                        return;
                    if (timedOut)
                        return;
                    if (modal) {
                        setAppKitReady(true);
                        subscribeToAppKit(modal);
                        await new Promise(r => setTimeout(r, 500));
                        if (isStaleConnectionOperation())
                            return;
                        const addr = await getConnectedAddress();
                        if (isStaleConnectionOperation())
                            return;
                        if (addr) {
                            handleAccountChange({ address: addr, isConnected: true });
                            console.log('[MobileAuth] Session restored from AppKit, address:', addr);
                        }
                    }
                    else {
                        setAppKitReady(true);
                    }
                }
                catch (e) {
                    if (isStaleConnectionOperation())
                        return;
                    console.error('[MobileAuth] AppKit lazy-load failed (non-blocking):', getAuthDiagnostic(e));
                    setAppKitReady(true);
                }
            }
            else if (hasExistingWcPairing()) {
                console.log('[MobileAuth] No session flags, but WC pairing found in localStorage — loading AppKit to try restoring...');
                try {
                    const { modal, timedOut } = await settleStartupAppKit(async (lateModal) => {
                        setAppKitReady(true);
                        subscribeToAppKit(lateModal);
                        const settleMs = /android/i.test(navigator.userAgent) ? 2000 : 1000;
                        await new Promise(r => setTimeout(r, settleMs));
                        if (isStaleConnectionOperation())
                            return;
                        const lateAddress = await getConnectedAddress();
                        if (!isStaleConnectionOperation() && lateAddress) {
                            handleAccountChange({ address: lateAddress, isConnected: true });
                        }
                    });
                    if (isStaleConnectionOperation())
                        return;
                    if (timedOut)
                        return;
                    if (modal) {
                        setAppKitReady(true);
                        subscribeToAppKit(modal);
                        const isAndroid = /android/i.test(navigator.userAgent);
                        await new Promise(r => setTimeout(r, isAndroid ? 2000 : 1000));
                        if (isStaleConnectionOperation())
                            return;
                        const addr = await getConnectedAddress();
                        if (isStaleConnectionOperation())
                            return;
                        if (addr) {
                            handleAccountChange({ address: addr, isConnected: true });
                            console.log('[MobileAuth] Session restored from existing WC pairing! Address:', addr);
                        }
                        else {
                            console.log('[MobileAuth] WC pairing found but session not yet active — will restore on connect()');
                        }
                    }
                    else {
                        setAppKitReady(true);
                    }
                }
                catch (e) {
                    if (isStaleConnectionOperation())
                        return;
                    console.warn('[MobileAuth] WC pairing restore failed (non-blocking):', getAuthDiagnostic(e));
                    setAppKitReady(true);
                }
            }
            else {
                console.log('[MobileAuth] No previous session and no WC pairing, skipping AppKit load until connect');
                setAppKitReady(true);
            }
        };
        let mounted = true;
        void initSession()
            .catch(error => {
            console.error('[MobileAuth] Unexpected session initialization failure:', getAuthDiagnostic(error));
            if (mounted) {
                setAppKitReady(true);
                setBackendAuthStatus('unavailable');
                setOnboardingError('Wallet session restoration failed. Please retry connecting to your wallet.');
            }
        })
            .finally(() => {
            // Account subscriptions may synchronously replay a connected account.
            // That transition intentionally advances the connection generation,
            // causing the initializer to return early as stale. Initialization is
            // nevertheless complete and must always release the route/login
            // spinner while this provider is still mounted.
            if (mounted)
                setSessionStorageChecked(true);
        });
        return () => {
            mounted = false;
            connectionOperationGuardRef.current.invalidate();
            appKitSubscriptionCleanupRef.current?.();
        };
    }, [subscribeToAppKit]);
    // Safety net: the session-init effect above runs only once at mount. If it
    // was skipped because we mounted on a guest (magic-link) route and the user
    // then SPA-navigates into the main application (history back/forward, or a
    // future in-app link that forgot to use exitGuestMode), the wallet layer was
    // never initialized. Force a full reload so it boots from scratch.
    useEffect(() => {
        if (!guestInitSkippedRef.current)
            return;
        if (isGuestPath(location.pathname))
            return;
        console.log('[MobileAuth] Left guest route via SPA navigation — reloading to initialize wallet layer');
        window.location.reload();
    }, [location.pathname]);
    useEffect(() => {
        if (!isReconnecting)
            return;
        if (!sessionStorageChecked)
            return;
        if (isConnected && address) {
            console.log('[MobileAuth] Reconnection detected after MetaMask return! Address:', address);
            sessionStorage.removeItem('mobile_connect_pending');
            setIsReconnecting(false);
            const pendingAuth = loadAuthInProgress();
            if (pendingAuth && pendingAuth.walletAddress === address.toLowerCase() && !jwt && !resumeScheduledRef.current) {
                console.log('[MobileAuth] Detected interrupted auth; waiting for explicit Continue');
                explicitOnboardingWalletRef.current = null;
                setInterruptedOnboarding(true);
                authTriggeredForAddress.current = address;
                setRequiresExplicitConfirmation(true);
                setOnboardingStatus('error');
                setOnboardingError('Wallet setup was interrupted. Tap Continue to resume.');
            }
            return;
        }
        if (reconnectInFlightRef.current) {
            console.log('[MobileAuth] Reconnect effect: another reconnect already in flight, skipping');
            return;
        }
        let cancelled = false;
        const reconnectOperation = connectionOperationGuardRef.current.begin();
        const isAndroid = /android/i.test(navigator.userAgent);
        const tryReconnect = async () => {
            reconnectInFlightRef.current = true;
            const maxAttempts = isAndroid ? 10 : 5;
            const retryDelay = isAndroid ? 2500 : 2000;
            console.log('[MobileAuth] Reconnect effect: forcing AppKit session restore...', isAndroid ? '(Android,' + maxAttempts + ' attempts)' : '');
            try {
                for (let attempt = 0; attempt < maxAttempts; attempt++) {
                    if (cancelled)
                        return;
                    const success = await forceReconnect();
                    if (cancelled || !connectionOperationGuardRef.current.isCurrent(reconnectOperation))
                        return;
                    if (success) {
                        console.log('[MobileAuth] Force reconnect succeeded on attempt', attempt + 1);
                        const addr = await getConnectedAddress();
                        if (!cancelled && connectionOperationGuardRef.current.isCurrent(reconnectOperation) && addr) {
                            reconnectInFlightRef.current = false;
                            transitionConnectedAccount(addr);
                        }
                        return;
                    }
                    console.log('[MobileAuth] Force reconnect attempt', attempt + 1, '/' + maxAttempts, '- not yet connected');
                    await new Promise(r => setTimeout(r, retryDelay));
                    if (cancelled || !connectionOperationGuardRef.current.isCurrent(reconnectOperation))
                        return;
                }
                if (!cancelled && connectionOperationGuardRef.current.isCurrent(reconnectOperation)) {
                    console.log('[MobileAuth] All reconnect attempts failed — ending expired wallet handoff');
                    endConnectHandoff('Wallet connection timed out. Please try again.');
                }
            }
            finally {
                if (connectionOperationGuardRef.current.isCurrent(reconnectOperation)) {
                    reconnectInFlightRef.current = false;
                }
            }
        };
        tryReconnect();
        const timeoutMs = isAndroid ? 45000 : 20000;
        const timeout = setTimeout(() => {
            if (!connectionOperationGuardRef.current.isCurrent(reconnectOperation))
                return;
            cancelled = true;
            console.log('[MobileAuth] Reconnection timeout — ending expired wallet handoff');
            endConnectHandoff('Wallet connection timed out. Please try again.');
        }, timeoutMs);
        return () => {
            cancelled = true;
            if (connectionOperationGuardRef.current.isCurrent(reconnectOperation)) {
                connectionOperationGuardRef.current.invalidate();
            }
            clearTimeout(timeout);
        };
    }, [isReconnecting, sessionStorageChecked, isConnected, address, jwt, transitionConnectedAccount, endConnectHandoff]);
    useEffect(() => {
        if (isConnected && address) {
            console.log('[MobileAuth] CONNECTION VERIFIED — address:', address);
            sessionStorage.setItem('dwdata_wallet_connected', 'true');
            if (sessionStorage.getItem('mobile_connect_pending')) {
                console.log('[MobileAuth] Clearing mobile_connect_pending (connection confirmed)');
                sessionStorage.removeItem('mobile_connect_pending');
            }
        }
        else if (!isConnected) {
            console.log('[MobileAuth] NOT CONNECTED');
        }
    }, [isConnected, address]);
    useEffect(() => {
        if (!sessionStorageChecked)
            return;
        if (jwt && biometricState === 'idle' && !biometricCheckStarted.current) {
            biometricCheckStarted.current = true;
            console.log('[MobileAuth] Biometric auto-verify disabled (iOS Safari compatibility), skipping to verified');
            setBiometricState('verified');
        }
    }, [sessionStorageChecked, jwt, biometricState]);
    useEffect(() => {
        if (isAuthenticated && sessionStorageChecked && biometricState === 'verified') {
            if (location.pathname === '/' || location.pathname === '/login') {
                const tcAccepted = localStorage.getItem('dwinity_tc_accepted') === 'true';
                if (!tcAccepted) {
                    console.log('[MobileAuth] JWT verified but T&C not accepted yet, staying on login page');
                    return;
                }
                let lastRoute = sessionStorage.getItem('mobile_last_route');
                if (lastRoute === '/' || lastRoute === '/login') {
                    lastRoute = null;
                }
                console.log('[MobileAuth] JWT + biometric verified, redirecting to:', lastRoute || '/explorer');
                navigate(lastRoute || '/explorer', { replace: true });
            }
        }
    }, [isAuthenticated, sessionStorageChecked, biometricState, navigate, location.pathname]);
    const runBiometricVerification = useCallback(async () => {
        // Biometric disabled — security policy requires full re-login after each session close
        setBiometricState('verified');
    }, []);
    const retryBiometric = useCallback(() => {
        runBiometricVerification();
    }, [runBiometricVerification]);
    const clearAllWcStorage = useCallback((includeIndexedDB = false) => {
        const prefixes = ['wc@2:', 'W3M', '@w3m', 'wagmi.', '-walletlink', 'WALLETCONNECT', 'walletconnect', '@appkit'];
        const sessionKeysToRemove = [];
        for (let i = 0; i < sessionStorage.length; i++) {
            const key = sessionStorage.key(i);
            if (key && prefixes.some(p => key.startsWith(p))) {
                sessionKeysToRemove.push(key);
            }
        }
        sessionKeysToRemove.forEach(key => sessionStorage.removeItem(key));
        const localKeysToRemove = [];
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && prefixes.some(p => key.startsWith(p))) {
                localKeysToRemove.push(key);
            }
        }
        localKeysToRemove.forEach(key => localStorage.removeItem(key));
        console.log('[MobileAuth] Cleared WC/AppKit keys:', sessionKeysToRemove.length, 'session +', localKeysToRemove.length, 'local');
        if (includeIndexedDB) {
            try {
                const dbNames = ['WALLET_CONNECT_V2_INDEXED_DB', 'wc@2', 'walletconnect'];
                dbNames.forEach(name => {
                    try {
                        indexedDB.deleteDatabase(name);
                        console.log('[MobileAuth] Deleted IndexedDB:', name);
                    }
                    catch { }
                });
            }
            catch { }
        }
    }, []);
    const resetAllAuthState = useCallback(() => {
        isAuthenticatingRef.current = false;
        explicitOnboardingWalletRef.current = null;
        biometricCheckStarted.current = false;
        authTriggeredForAddress.current = null;
        setOnboardingStatus('idle');
        setOnboardingError(null);
        setInterruptedOnboarding(false);
        setBiometricState('idle');
        setNeedsManualSign(false);
        setRequiresExplicitConfirmation(false);
        setSignatureCount(0);
        setWaitingForSignatures(false);
    }, []);
    useEffect(() => {
        const handleAuthInvalidated = (e) => {
            const detail = e.detail;
            // The sender has already removed credentials. Check the clearing's
            // generation instead of trying to reconstruct a binding from storage.
            if (!isAuthInvalidationCurrent(detail)
                || !jwtRef.current
                || !liveConnectedRef.current
                || liveAddressRef.current?.toLowerCase() !== detail.normalizedWallet)
                return;
            console.log('[MobileAuth] Auth invalidated event received — clearing state and redirecting to login');
            jwtRef.current = null;
            setJwt(null);
            invalidateRefreshBinding();
            setAuthoritativeDenial(null);
            setBackendAuthStatus(liveConnectedRef.current && liveAddressRef.current ? 'checking' : 'idle');
            resetAllAuthState();
            navigate('/login', { replace: true });
        };
        const handleAuthRefreshed = (e) => {
            const nextJwt = e.detail?.jwt;
            // This event only synchronizes an existing live session with credentials
            // already committed by refresh. It must not establish a session or replay
            // an older credential, even after re-login to the same wallet.
            // Read live refs: account/disconnect callbacks update them before React
            // renders. Do not compare against the previous refresh binding, since a
            // legitimate refresh-token rotation invalidates that binding.
            if (typeof nextJwt !== 'string'
                || !nextJwt
                || loadJwt() !== nextJwt
                || !jwtRef.current
                || !liveConnectedRef.current
                || !liveAddressRef.current
                || loadWalletAddress()?.toLowerCase() !== liveAddressRef.current.toLowerCase())
                return;
            console.log('[MobileAuth] Auth refreshed event received — updating JWT in state');
            setJwt(nextJwt);
        };
        window.addEventListener('mobile-auth-invalidated', handleAuthInvalidated);
        window.addEventListener('mobile-auth-refreshed', handleAuthRefreshed);
        return () => {
            window.removeEventListener('mobile-auth-invalidated', handleAuthInvalidated);
            window.removeEventListener('mobile-auth-refreshed', handleAuthRefreshed);
        };
    }, [navigate, resetAllAuthState, isConnected, address]);
    const connect = useCallback(async () => {
        console.log('[MobileAuth] Connect called...');
        let isEmbedded = false;
        try {
            isEmbedded = window.self !== window.top;
        }
        catch {
            isEmbedded = true;
        }
        if (shouldUseInjectedMetaMaskPreview(window.location.hostname, window.location.search, isEmbedded)) {
            const ethereum = window.ethereum;
            const providers = Array.isArray(ethereum?.providers)
                ? ethereum.providers
                : ethereum
                    ? [ethereum]
                    : [];
            const provider = providers.find((candidate) => candidate?.isMetaMask && !candidate?.isBraveWallet) ||
                providers.find((candidate) => candidate?.isMetaMask);
            if (!provider) {
                setError(new Error('The MetaMask browser extension is not available in this tab.'));
                return;
            }
            if (connectingRef.current) {
                console.log('[MobileAuth] Injected MetaMask connect already in progress, skipping');
                return;
            }
            connectingRef.current = true;
            isConnectingRef.current = true;
            explicitOnboardingWalletRef.current = '__pending_connect__';
            setIsConnecting(true);
            setError(null);
            try {
                let accounts = await provider.request({ method: 'eth_accounts' });
                if (!Array.isArray(accounts) || accounts.length === 0) {
                    accounts = await Promise.race([
                        provider.request({ method: 'eth_requestAccounts' }),
                        new Promise((_, reject) => {
                            setTimeout(() => reject(new Error('MetaMask connection request timed out.')), 45000);
                        }),
                    ]);
                }
                const connectedAddress = Array.isArray(accounts) ? accounts[0] : null;
                if (!connectedAddress) {
                    throw new Error('MetaMask did not return an account.');
                }
                setInjectedWalletProvider(provider);
                injectedProviderCleanupRef.current?.();
                if (typeof provider.on === 'function') {
                    const handleInjectedAccountsChanged = (nextAccounts) => {
                        handleAccountChange({
                            isConnected: Array.isArray(nextAccounts) && nextAccounts.length > 0,
                            address: nextAccounts?.[0],
                        });
                    };
                    provider.on('accountsChanged', handleInjectedAccountsChanged);
                    injectedProviderCleanupRef.current = () => {
                        provider.removeListener?.('accountsChanged', handleInjectedAccountsChanged);
                    };
                }
                transitionConnectedAccount(connectedAddress);
                sessionStorage.removeItem('mobile_connect_pending');
                return;
            }
            catch (e) {
                setInjectedWalletProvider(null);
                explicitOnboardingWalletRef.current = null;
                const code = e?.code;
                setError(new Error(code === -32002
                    ? 'MetaMask still has a hidden pending request. Fully close and restart the browser, then try again.'
                    : authErrorMessage(e)));
                return;
            }
            finally {
                connectingRef.current = false;
                isConnectingRef.current = false;
                setIsConnecting(false);
            }
        }
        if (!projectId) {
            console.error('[MobileAuth] No WalletConnect project ID configured!');
            setError(new Error('Wallet connection is not configured. Please try again later.'));
            return;
        }
        const now = Date.now();
        if (shouldRecoverStaleConnectLock({
            locked: connectingRef.current,
            visible: isConnectingRef.current,
            reconnectInFlight: reconnectInFlightRef.current,
        })) {
            console.warn('[MobileAuth] Recovering stale connect lock');
            connectingRef.current = false;
            lastConnectTime.current = 0;
        }
        if (connectingRef.current) {
            console.log('[MobileAuth] Connect already in progress, skipping');
            return;
        }
        if (now - lastConnectTime.current < 2000) {
            console.log('[MobileAuth] Connect debounced (< 2s since last accepted call)');
            return;
        }
        lastConnectTime.current = now;
        // A wallet-signing flow may only be entered after a direct user action.
        // Passive AppKit restoration and normal page refresh must never inherit
        // permission to open MetaMask.
        explicitOnboardingWalletRef.current = '__pending_connect__';
        connectingRef.current = true;
        isConnectingRef.current = true;
        const connectAttempt = ++connectAttemptRef.current;
        // AppKit often replays its cached disconnected account asynchronously.
        // Keep this guard for the whole pre-connection generation, not merely the
        // subscription microtask.
        ignoredDisconnectedSnapshotAttemptRef.current = connectAttempt;
        // This is deliberately the first state transition after accepting a tap.
        setIsConnecting(true);
        setError(null);
        const connectionOperation = connectionOperationGuardRef.current.begin();
        const isStaleConnectionOperation = () => !connectionOperationGuardRef.current.isCurrent(connectionOperation);
        const isAndroid = /android/i.test(navigator.userAgent);
        let walletReturnHandoffActive = false;
        try {
            console.log('[MobileAuth] Step 1: Check if AppKit already has a live session...');
            try {
                const existingModal = await initAppKit();
                if (isStaleConnectionOperation())
                    return;
                if (existingModal) {
                    subscribeToAppKit(existingModal);
                    setAppKitReady(true);
                    await new Promise(r => setTimeout(r, isAndroid ? 1000 : 300));
                    if (isStaleConnectionOperation())
                        return;
                    const existingAddr = await getConnectedAddress();
                    if (isStaleConnectionOperation())
                        return;
                    if (existingAddr) {
                        transitionConnectedAccount(existingAddr);
                        sessionStorage.setItem('dwdata_wallet_connected', 'true');
                        sessionStorage.removeItem('mobile_connect_pending');
                        setIsReconnecting(false);
                        authTriggeredForAddress.current = null;
                        return;
                    }
                }
            }
            catch (e) {
                if (isStaleConnectionOperation())
                    return;
                console.warn('[MobileAuth] Existing session check failed (non-blocking):', getAuthDiagnostic(e));
            }
            const pending = sessionStorage.getItem('mobile_connect_pending');
            const pendingAge = pending ? Date.now() - parseInt(pending, 10) : Infinity;
            const isReturningFromWallet = !!pending && pendingAge < 120000;
            if (isReturningFromWallet) {
                if (reconnectInFlightRef.current) {
                    // The visibility-return effect owns this pending wallet handoff.
                    walletReturnHandoffActive = true;
                    return;
                }
                reconnectInFlightRef.current = true;
                const maxAttempts = isAndroid ? 12 : 6;
                const pollDelay = isAndroid ? 2500 : 2000;
                setIsReconnecting(true);
                try {
                    await forceReconnect();
                    if (isStaleConnectionOperation())
                        return;
                    await new Promise(r => setTimeout(r, 1000));
                    for (let i = 0; i < maxAttempts; i++) {
                        const addr = await getConnectedAddress();
                        if (isStaleConnectionOperation())
                            return;
                        if (addr) {
                            transitionConnectedAccount(addr);
                            sessionStorage.removeItem('mobile_connect_pending');
                            setIsReconnecting(false);
                            authTriggeredForAddress.current = null;
                            return;
                        }
                        if (i === Math.floor(maxAttempts / 2))
                            await forceReconnect();
                        await new Promise(r => setTimeout(r, pollDelay));
                        if (isStaleConnectionOperation())
                            return;
                    }
                    endConnectHandoff('Wallet connection timed out. Please try again.');
                }
                catch (e) {
                    if (!isStaleConnectionOperation()) {
                        console.warn('[MobileAuth] Wallet-return restore failed:', getAuthDiagnostic(e));
                        endConnectHandoff(authErrorMessage(e));
                    }
                }
                finally {
                    reconnectInFlightRef.current = false;
                }
                return;
            }
            resetAllAuthState();
            explicitOnboardingWalletRef.current = '__pending_connect__';
            const existingAddr = await getConnectedAddress();
            if (isStaleConnectionOperation())
                return;
            if (existingAddr) {
                transitionConnectedAccount(existingAddr);
                sessionStorage.setItem('dwdata_wallet_connected', 'true');
                sessionStorage.removeItem('mobile_connect_pending');
                authTriggeredForAddress.current = null;
                return;
            }
            if (isConnected) {
                const { healthy } = await testProviderHealth();
                if (isStaleConnectionOperation())
                    return;
                if (healthy)
                    return;
                const modal = getAppKitSync();
                if (modal) {
                    try {
                        await modal.disconnect();
                    }
                    catch { }
                    if (isStaleConnectionOperation())
                        return;
                }
                handleAccountChange({ isConnected: false });
                await new Promise(r => setTimeout(r, 300));
                if (isStaleConnectionOperation())
                    return;
            }
            if (hasExistingWcPairing()) {
                try {
                    await forceReconnect();
                    if (isStaleConnectionOperation())
                        return;
                    for (let i = 0; i < EXPLICIT_PAIRING_RESTORE_ATTEMPTS; i++) {
                        const pairedAddr = await getConnectedAddress();
                        if (isStaleConnectionOperation())
                            return;
                        if (pairedAddr) {
                            transitionConnectedAccount(pairedAddr);
                            sessionStorage.setItem('dwdata_wallet_connected', 'true');
                            authTriggeredForAddress.current = null;
                            return;
                        }
                        await new Promise(r => setTimeout(r, 400));
                    }
                }
                catch (e) {
                    if (isStaleConnectionOperation())
                        return;
                    console.warn('[MobileAuth] Short pairing restore failed:', getAuthDiagnostic(e));
                }
                clearAllWcStorage(true);
                await new Promise(r => setTimeout(r, 100));
                if (isStaleConnectionOperation())
                    return;
            }
            const modal = await initAppKit();
            if (isStaleConnectionOperation())
                return;
            if (!modal)
                throw new Error('Wallet initialization failed. Please try again.');
            subscribeToAppKit(modal);
            setAppKitReady(true);
            sessionStorage.setItem('mobile_connect_pending', Date.now().toString());
            walletReturnHandoffActive = true;
            await modal.open({ view: 'Connect' });
            if (isStaleConnectionOperation()) {
                walletReturnHandoffActive = false;
                return;
            }
            // AppKit's modal opens asynchronously. Once it has opened, only a real
            // close/cancel event, a connected account, or this bounded fallback owns
            // the lock; `open()` resolving alone is not a successful connection.
            // `open()` resolving establishes this generation's modal. A state
            // subscription can synchronously replay `{ open: false }`, however;
            // suppress only that setup replay rather than treating it as a cancel.
            let modalOpenEstablished = true;
            let subscribingToState = false;
            const closeIfStillPending = (state) => {
                const isOpen = state?.open ?? state?.isOpen;
                if (subscribingToState)
                    return;
                if (isOpen === true) {
                    modalOpenEstablished = true;
                    return;
                }
                if (modalOpenEstablished && isOpen === false) {
                    endConnectHandoff('Wallet connection was cancelled. Please try again.', connectAttempt);
                }
            };
            const eventCloseIfStillPending = (event) => {
                const name = String(event?.data?.event || event?.event || event?.type || '').toLowerCase();
                if (modalOpenEstablished && (name.includes('close') || name.includes('cancel'))) {
                    endConnectHandoff('Wallet connection was cancelled. Please try again.', connectAttempt);
                }
            };
            const cleanups = [];
            const timeout = setTimeout(() => {
                endConnectHandoff('Wallet connection timed out. Please try again.', connectAttempt);
            }, 45000);
            // Register ownership before subscribing: some implementations synchronously
            // publish their current (already closed) state in subscribeState().
            connectHandoffRef.current = {
                attempt: connectAttempt,
                cleanup: () => cleanups.forEach(cleanup => cleanup()),
                timeout,
            };
            if (typeof modal.subscribeState === 'function') {
                subscribingToState = true;
                const unsubscribe = modal.subscribeState(closeIfStillPending);
                subscribingToState = false;
                if (typeof unsubscribe === 'function')
                    cleanups.push(unsubscribe);
            }
            if (typeof modal.subscribeEvents === 'function') {
                const unsubscribe = modal.subscribeEvents(eventCloseIfStillPending);
                if (typeof unsubscribe === 'function')
                    cleanups.push(unsubscribe);
            }
        }
        catch (e) {
            // A connected account (or a newer attempt) invalidates this operation.
            // Do not let a late rejection from AppKit.open overwrite that success.
            if (connectAttemptRef.current !== connectAttempt || isStaleConnectionOperation())
                return;
            console.error('[MobileAuth] AppKit modal open() error:', getAuthDiagnostic(e));
            walletReturnHandoffActive = false;
            sessionStorage.removeItem('mobile_connect_pending');
            setError(new Error(authErrorMessage(e)));
        }
        finally {
            if (connectAttemptRef.current === connectAttempt && !walletReturnHandoffActive) {
                connectingRef.current = false;
                isConnectingRef.current = false;
                setIsConnecting(false);
                setIsReconnecting(false);
                sessionStorage.removeItem('mobile_connect_pending');
            }
        }
    }, [isConnected, clearAllWcStorage, resetAllAuthState, handleAccountChange, transitionConnectedAccount, endConnectHandoff]);
    const cancelConnect = useCallback(() => {
        endConnectHandoff();
    }, [endConnectHandoff]);
    const softDisconnect = useCallback(async () => {
        console.log('[MobileAuth] Soft disconnect — clearing WC + JWT, keeping MPC data for fast re-login');
        endConnectHandoff();
        connectionOperationGuardRef.current.invalidate();
        setIsReconnecting(false);
        reconnectInFlightRef.current = false;
        liveAddressRef.current = undefined;
        liveConnectedRef.current = false;
        isAuthenticatingRef.current = false;
        setAddress(undefined);
        setIsConnected(false);
        injectedProviderCleanupRef.current?.();
        injectedProviderCleanupRef.current = null;
        setInjectedWalletProvider(null);
        localStorage.removeItem('jwt');
        invalidateRefreshBinding();
        setJwt(null);
        setAuthoritativeDenial(null);
        setWhitelistStatus('idle');
        setWhitelistUnavailableReason(null);
        setBackendAuthStatus('idle');
        resetAllAuthState();
        sessionStorage.removeItem('mobile_connect_pending');
        sessionStorage.removeItem('dwdata_wallet_connected');
        clearAuthInProgress();
        localStorage.removeItem('mobile_background_at');
        clearAllWcStorage(true);
        navigate('/login', { replace: true });
        const modal = getAppKitSync();
        if (modal) {
            try {
                await modal.disconnect();
            }
            catch (e) {
                console.warn('[MobileAuth] AppKit disconnect error (ignored):', getAuthDiagnostic(e));
            }
        }
        console.log('[MobileAuth] Soft disconnect complete — MPC data preserved, navigating to login');
    }, [clearAllWcStorage, navigate, resetAllAuthState, endConnectHandoff]);
    const disconnect = useCallback(async () => {
        console.log('[MobileAuth] Full disconnect – clearing ALL data (MPC + WC)');
        endConnectHandoff();
        connectionOperationGuardRef.current.invalidate();
        setIsReconnecting(false);
        reconnectInFlightRef.current = false;
        liveAddressRef.current = undefined;
        liveConnectedRef.current = false;
        isAuthenticatingRef.current = false;
        setAddress(undefined);
        setIsConnected(false);
        injectedProviderCleanupRef.current?.();
        injectedProviderCleanupRef.current = null;
        setInjectedWalletProvider(null);
        clearAllMobileAuthData();
        invalidateRefreshBinding();
        clearKeySignature();
        clearSessionCreated();
        clearAuthInProgress();
        setJwt(null);
        setAuthoritativeDenial(null);
        setWhitelistStatus('idle');
        setWhitelistUnavailableReason(null);
        setBackendAuthStatus('idle');
        resetAllAuthState();
        sessionStorage.removeItem('mobile_terms_accepted');
        sessionStorage.removeItem('mobile_last_route');
        sessionStorage.removeItem('mobile_explorer_path');
        sessionStorage.removeItem('mobile_connect_pending');
        sessionStorage.removeItem('dwdata_wallet_connected');
        sessionStorage.removeItem('userKey');
        clearAuthErrorDiagnostics();
        localStorage.removeItem('mobile_background_at');
        clearAllWcStorage(true);
        navigate('/login', { replace: true });
        const modal = getAppKitSync();
        if (modal) {
            try {
                await modal.disconnect();
            }
            catch (e) {
                console.warn('[MobileAuth] AppKit disconnect error (ignored):', getAuthDiagnostic(e));
            }
        }
        console.log('[MobileAuth] Full disconnect complete – navigating to login');
    }, [clearAllWcStorage, navigate, resetAllAuthState, endConnectHandoff]);
    const waitForForegroundAndHealthy = useCallback(async (label, authSession) => {
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
        const waitMs = isIOS ? 5000 : 3000;
        console.log(`[MobileAuth] ${label}: waiting ${waitMs}ms for WC relay to stabilize...`);
        await new Promise(r => setTimeout(r, waitMs));
        authSession.assertCurrent();
        if (document.hidden) {
            console.log(`[MobileAuth] ${label}: waiting for app to return to foreground...`);
            await new Promise(resolve => {
                const handler = () => {
                    if (!document.hidden) {
                        document.removeEventListener('visibilitychange', handler);
                        setTimeout(resolve, isIOS ? 2000 : 1000);
                    }
                };
                document.addEventListener('visibilitychange', handler);
                setTimeout(() => {
                    document.removeEventListener('visibilitychange', handler);
                    resolve();
                }, 60000);
            });
            authSession.assertCurrent();
        }
        const { healthy } = await testProviderHealth();
        authSession.assertCurrent();
        if (!healthy) {
            console.log(`[MobileAuth] ${label}: provider unhealthy, attempting reconnect...`);
            const restored = await forceReconnect();
            authSession.assertCurrent();
            if (!restored) {
                throw new Error('WalletConnect session lost. Please try again.');
            }
            await new Promise(r => setTimeout(r, 2000));
            authSession.assertCurrent();
        }
    }, []);
    const runFullSignatureFlow = useCallback(async (walletAddress, authSession) => {
        authSession.assertCurrent();
        const hasSavedKey = hasSavedSymmetricKey();
        const apiVersion = process.env.API_VERSION || '1.0';
        if (!hasSavedKey) {
            console.log('[MobileAuth] No saved symmetric key — sequential pre-signing (symmetric key, then recovery)...');
            authSession.commit(() => setOnboardingStatus('recovering'));
            const { healthy: provHealthy } = await testProviderHealth();
            authSession.assertCurrent();
            if (!provHealthy) {
                console.log('[MobileAuth] Provider unhealthy before sig 1, attempting reconnect...');
                const restored = await forceReconnect();
                authSession.assertCurrent();
                if (!restored) {
                    throw new Error('WalletConnect provider is not responding. Please reconnect your wallet and try again.');
                }
                await new Promise(r => setTimeout(r, 1500));
                authSession.assertCurrent();
            }
            console.log('[MobileAuth] SIG 1/2: Requesting symmetric key signature from MetaMask...');
            authSession.commit(() => setSignatureCount(1));
            const symmetricKeyMsg = getSymmetricKeyChallenge(walletAddress);
            const symSig = await wcSignMessage(symmetricKeyMsg, walletAddress);
            authSession.assertCurrent();
            console.log('[MobileAuth] SIG 1/2: Symmetric key signature received, length:', symSig.length);
            authSession.commit(() => storeKeySignature(symSig));
            await waitForForegroundAndHealthy('SIG 2/2 prep', authSession);
            authSession.assertCurrent();
            console.log('[MobileAuth] SIG 2/2: Requesting recovery signature from MetaMask...');
            authSession.commit(() => setSignatureCount(2));
            const timestamp = generateExpirationTimeStamp();
            const recoverSendObject = {
                method: 'get',
                endpoint: `/api/${apiVersion}/mpc/recover`,
                ncwa: walletAddress,
            };
            const recoverMsg = buildEndpointMessage(walletAddress, recoverSendObject, timestamp);
            const recoverSig = await wcSignMessage(recoverMsg, walletAddress);
            authSession.assertCurrent();
            console.log('[MobileAuth] SIG 2/2: Recovery signature received, length:', recoverSig.length);
            authSession.commit(() => setPreSignedForEndpoint(`/api/${apiVersion}/mpc/recover`, recoverMsg, recoverSig, timestamp));
            authSession.commit(() => setWaitingForSignatures(true));
            console.log('[MobileAuth] Both signatures obtained sequentially, proceeding with login flow...');
        }
        else {
            console.log('[MobileAuth] Symmetric key already saved, no pre-signing needed');
        }
        let localSignCount = 0;
        const signMessageAsync = async (args) => {
            localSignCount++;
            authSession.commit(() => {
                setSignatureCount(localSignCount);
                setWaitingForSignatures(false);
            });
            if (localSignCount === 1) {
                const { healthy } = await testProviderHealth();
                authSession.assertCurrent();
                if (!healthy) {
                    console.log('[MobileAuth] Provider unhealthy before sig, attempting reconnect...');
                    const restored = await forceReconnect();
                    authSession.assertCurrent();
                    if (!restored) {
                        throw new Error('WalletConnect provider is not responding. Please reconnect your wallet and try again.');
                    }
                    await new Promise(r => setTimeout(r, 1500));
                    authSession.assertCurrent();
                }
            }
            else {
                await waitForForegroundAndHealthy(`sig ${localSignCount} prep`, authSession);
                authSession.assertCurrent();
            }
            console.log('[MobileAuth] Requesting signature', localSignCount, 'via WalletConnect...');
            const sig = await wcSignMessage(args.message, walletAddress);
            authSession.assertCurrent();
            console.log('[MobileAuth] Signature', localSignCount, 'received, length:', sig.length);
            return sig;
        };
        authSession.commit(() => {
            setSignatureCount(0);
            setOnboardingStatus('recovering');
        });
        console.log('[MobileAuth] Starting fullLoginProcess... (sequential)');
        const result = await fullLoginProcess(signMessageAsync, walletAddress, authSession.assertCurrent);
        authSession.assertCurrent();
        if (result.authToken) {
            console.log('[MobileAuth] Auth token received!');
            authSession.commit(() => {
                storeWalletAddress(walletAddress);
                clearAuthInProgress();
                localStorage.setItem('dwinity_tc_accepted', 'true');
                sessionStorage.removeItem('mobile_terms_accepted');
                markSessionCreated();
                setJwt(result.authToken);
                setBackendAuthStatus('authorized');
                setRequiresExplicitConfirmation(false);
                explicitOnboardingWalletRef.current = null;
                setOnboardingStatus('complete');
                sessionStorage.removeItem('mobile_connect_pending');
                let lastRoute = sessionStorage.getItem('mobile_last_route');
                if (lastRoute)
                    lastRoute = lastRoute.replace(/^\/mobile/, '');
                if (!lastRoute || lastRoute === '/' || lastRoute === '/login')
                    lastRoute = null;
                navigate(lastRoute || '/explorer', { replace: true });
                console.log('[MobileAuth] Full auth process completed, navigated to explorer');
                setBiometricState('verified');
            });
        }
        else {
            throw new Error('No auth token received from login process');
        }
    }, [navigate]);
    const runAuthProcess = useCallback(async () => {
        if (!address || !isConnected) {
            console.log('[MobileAuth] runAuthProcess: wallet not connected', { address: !!address, isConnected });
            return;
        }
        if (whitelistStatus !== 'authorized') {
            if (whitelistStatus === 'unavailable') {
                setOnboardingError('Whitelist verification is temporarily unavailable. Please retry.');
                setOnboardingStatus('error');
            }
            return;
        }
        if (isAuthenticatingRef.current) {
            console.log('[MobileAuth] runAuthProcess: already authenticating, skipping');
            return;
        }
        const authOperation = connectionOperationGuardRef.current.begin();
        const authSession = createGuardedConnectionSession(connectionOperationGuardRef.current, authOperation, address, () => ({
            address: liveAddressRef.current,
            isConnected: liveConnectedRef.current,
        }));
        if (!authSession.commit(() => {
            isAuthenticatingRef.current = true;
            setAuthoritativeDenial(null);
            setBackendAuthStatus('checking');
            setInterruptedOnboarding(false);
            setOnboardingError(null);
            setOnboardingStatus('connecting');
            setSignatureCount(0);
        }))
            return;
        try {
            const normalizedAddress = address.toLowerCase();
            const hasExplicitOnboardingGrant = explicitOnboardingWalletRef.current === normalizedAddress;
            // Consent is one-shot. Even a failed or interrupted auth attempt must
            // require a new direct user action before another wallet prompt.
            explicitOnboardingWalletRef.current = null;
            const storedAddress = loadWalletAddress()?.toLowerCase();
            // Silent restore always precedes provider health checks and wallet
            // deep-links. Stored authorization is usable only for the exact live
            // AppKit account.
            if (storedAddress && storedAddress !== normalizedAddress) {
                authSession.commit(() => {
                    clearAllMobileAuthData();
                    invalidateRefreshBinding();
                    clearKeySignature();
                    clearSessionCreated();
                    clearAuthInProgress();
                    clearPreSignedCache();
                    setJwt(null);
                    setRequiresExplicitConfirmation(false);
                });
            }
            else if (storedAddress === normalizedAddress) {
                // Never authorize a persisted access token from its expiry alone. A
                // wallet-scoped refresh is the prompt-free server-authoritative check.
                if (loadRefreshToken()) {
                    const refreshResult = await attemptRefreshToken(normalizedAddress);
                    authSession.assertCurrent();
                    if (refreshResult.status === 'success') {
                        authSession.commit(() => {
                            clearAuthInProgress();
                            setJwt(refreshResult.jwt);
                            setBackendAuthStatus('authorized');
                            setOnboardingStatus('complete');
                            setBiometricState('verified');
                            setRequiresExplicitConfirmation(false);
                            explicitOnboardingWalletRef.current = null;
                        });
                        return;
                    }
                    if (refreshResult.status === 'unavailable') {
                        authSession.commit(() => {
                            setBackendAuthStatus('unavailable');
                            setOnboardingStatus('error');
                            setOnboardingError('Login service is temporarily unavailable. Please retry.');
                        });
                        return;
                    }
                    if (refreshResult.status === 'stale')
                        return;
                    if (refreshResult.status === 'wallet_mismatch')
                        return;
                    if (refreshResult.status === 'rejected') {
                        authSession.commit(() => {
                            // The wallet guard and the immutable refresh binding must both
                            // still match immediately before destructive revocation.
                            if (!isRefreshBindingCurrent(refreshResult.binding))
                                return;
                            clearAllMobileAuthData();
                            invalidateRefreshBinding();
                            clearKeySignature();
                            clearSessionCreated();
                            clearAuthInProgress();
                            clearPreSignedCache();
                            setJwt(null);
                            explicitOnboardingWalletRef.current = null;
                            // Refresh credentials authenticate a saved session, not contract
                            // membership. A 401 must not permanently label the wallet denied.
                            // Stop here: only a new user action may start signed backend auth,
                            // and runAuthProcess still requires the canonical whitelist.
                            if (refreshResult.httpStatus === 401) {
                                setRequiresExplicitConfirmation(true);
                                setAuthoritativeDenial(null);
                                setBackendAuthStatus('idle');
                                setOnboardingStatus('error');
                                setOnboardingError('Your saved session could not be restored. Tap Continue to sign in again.');
                                return;
                            }
                            // A refresh 403 has no verified credential-only error contract.
                            // Preserve the backend block rather than assuming it is expiry.
                            setRequiresExplicitConfirmation(false);
                            setAuthoritativeDenial({
                                walletAddress: normalizedAddress,
                                operation: authOperation,
                            });
                            setBackendAuthStatus('denied');
                            setOnboardingStatus('error');
                            setOnboardingError('This wallet is not authorized.');
                        });
                        return;
                    }
                }
                const savedAuthToken = loadJwt();
                if (!loadRefreshToken() && savedAuthToken && !hasExplicitOnboardingGrant) {
                    const validationResult = await validateStoredAccessToken(normalizedAddress);
                    authSession.assertCurrent();
                    if (loadJwt() !== savedAuthToken)
                        return;
                    if (validationResult.status === 'success') {
                        authSession.commit(() => {
                            setJwt(savedAuthToken);
                            setBackendAuthStatus('authorized');
                            setOnboardingStatus('complete');
                            setBiometricState('verified');
                            setRequiresExplicitConfirmation(false);
                        });
                        return;
                    }
                    if (validationResult.status === 'unavailable') {
                        authSession.commit(() => {
                            setBackendAuthStatus('unavailable');
                            setOnboardingStatus('error');
                            setOnboardingError('Login service is temporarily unavailable. Please retry.');
                        });
                        return;
                    }
                    if (validationResult.status === 'rejected') {
                        authSession.commit(() => {
                            clearAllMobileAuthData();
                            invalidateRefreshBinding();
                            clearKeySignature();
                            clearSessionCreated();
                            clearAuthInProgress();
                            clearPreSignedCache();
                            setJwt(null);
                            setBackendAuthStatus('idle');
                            setOnboardingStatus('error');
                            setRequiresExplicitConfirmation(true);
                        });
                        return;
                    }
                }
                // With no refresh credential there is no prompt-free authoritative
                // validation endpoint available in this client. Continue explicitly;
                // do not treat an unexpired local JWT as authenticated.
                if (loadJwt() && !isJwtExpired(loadJwt()) && !hasExplicitOnboardingGrant) {
                    authSession.commit(() => {
                        setBackendAuthStatus('idle');
                        setOnboardingStatus('error');
                        setOnboardingError('Please confirm your wallet to continue.');
                        setRequiresExplicitConfirmation(true);
                    });
                    return;
                }
            }
            if (!hasExplicitOnboardingGrant) {
                authSession.commit(() => {
                    setInterruptedOnboarding(!!loadAuthInProgress());
                    setRequiresExplicitConfirmation(true);
                    setBackendAuthStatus('idle');
                    setOnboardingStatus('error');
                    setOnboardingError(loadAuthInProgress()
                        ? 'Wallet setup was interrupted. Tap Continue to resume.'
                        : 'Your saved session could not be restored. Tap Continue to sign in again.');
                });
                return;
            }
            // There is definitively no valid/restorable authorization. Only now may
            // genuine, explicitly confirmed onboarding touch the wallet provider.
            authSession.commit(() => saveAuthInProgress(address));
            if (document.hidden) {
                console.log('[MobileAuth] Page is hidden, waiting for visibility...');
                await new Promise(resolve => {
                    const handler = () => {
                        if (!document.hidden) {
                            document.removeEventListener('visibilitychange', handler);
                            setTimeout(resolve, 1000);
                        }
                    };
                    document.addEventListener('visibilitychange', handler);
                    setTimeout(() => {
                        document.removeEventListener('visibilitychange', handler);
                        resolve();
                    }, 30000);
                });
                authSession.assertCurrent();
            }
            await runFullSignatureFlow(address, authSession);
            authSession.assertCurrent();
        }
        catch (err) {
            if (err instanceof StaleConnectionOperationError || !authSession.isCurrent())
                return;
            const diagnostic = getAuthDiagnostic(err);
            console.error('[MobileAuth] Auth process failed:', diagnostic);
            authSession.assertCurrent();
            const displayError = authErrorMessage(err);
            const backendDenied = shouldClearWalletScopedDataAfterBackendFailure(diagnostic.status ?? undefined);
            authSession.commit(() => {
                clearPreSignedCache();
                clearAuthInProgress();
                if (backendDenied) {
                    clearAllMobileAuthData();
                    invalidateRefreshBinding();
                    clearKeySignature();
                    clearSessionCreated();
                    setRequiresExplicitConfirmation(false);
                    explicitOnboardingWalletRef.current = null;
                }
                persistAuthFailure(err);
                setOnboardingError(displayError);
                setOnboardingStatus('error');
                setAuthoritativeDenial(backendDenied ? {
                    walletAddress: address.toLowerCase(),
                    operation: authOperation,
                } : null);
                setBackendAuthStatus(backendDenied ? 'denied' : 'unavailable');
                sessionStorage.removeItem('mobile_connect_pending');
            });
        }
        finally {
            authSession.commit(() => {
                isAuthenticatingRef.current = false;
            });
        }
    }, [address, isConnected, navigate, whitelistStatus]);
    useEffect(() => {
        runAuthProcessRef.current = runAuthProcess;
    }, [runAuthProcess]);
    useEffect(() => {
        if (backendAuthStatus === 'authorized' || backendAuthStatus === 'denied') {
            setSilentAuthRetryAttempt(0);
            return;
        }
        if (backendAuthStatus !== 'unavailable'
            || whitelistStatus !== 'authorized'
            || !address
            || !isConnected
            || (!loadRefreshToken() && !loadJwt()))
            return;
        const retryDelay = Math.min(15000, 1500 * (2 ** Math.min(silentAuthRetryAttempt, 3)));
        const retryTimer = window.setTimeout(() => {
            setSilentAuthRetryAttempt((attempt) => attempt + 1);
            runAuthProcessRef.current();
        }, retryDelay);
        return () => window.clearTimeout(retryTimer);
    }, [address, backendAuthStatus, isConnected, silentAuthRetryAttempt, whitelistStatus]);
    useEffect(() => {
        const handleBeforeUnload = () => {
            if (isAuthenticatingRef.current && address) {
                console.log('[MobileAuth] Page unloading during auth, saving state...');
                saveAuthInProgress(address);
                return;
            }
            // Mobile Safari and installed PWAs can emit pagehide/beforeunload
            // without a preceding visibilitychange when the page is suspended or
            // evicted.  Persist the background marker here as well so the session
            // timeout still applies after the page is restored.
            const isInAuthFlow = isAuthenticatingRef.current
                || !!sessionStorage.getItem('mobile_connect_pending')
                || !!loadAuthInProgress();
            if (!isInAuthFlow && (jwt || loadJwt())) {
                const now = Date.now();
                backgroundTimestampRef.current = now;
                localStorage.setItem('mobile_background_at', String(now));
                console.log('[MobileAuth] Page unloading while authenticated, background timestamp saved:', now);
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        window.addEventListener('pagehide', handleBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            window.removeEventListener('pagehide', handleBeforeUnload);
        };
    }, [address, jwt]);
    const backgroundTimestampRef = useRef(null);
    const SESSION_BACKGROUND_TIMEOUT_MS = 1200000;
    useEffect(() => {
        const handleVisibilityChange = async () => {
            if (document.hidden) {
                const isInAuthFlow = isAuthenticatingRef.current || !!sessionStorage.getItem('mobile_connect_pending') || !!loadAuthInProgress();
                if (!isInAuthFlow && (jwt || loadJwt())) {
                    const now = Date.now();
                    backgroundTimestampRef.current = now;
                    localStorage.setItem('mobile_background_at', String(now));
                    console.log('[MobileAuth] App going to background, timestamp saved:', now);
                }
                return;
            }
            const transientDisconnect = transientWalletDisconnectRef.current;
            if (transientDisconnect) {
                transientWalletDisconnectRef.current = null;
                await new Promise(r => setTimeout(r, 500));
                let restoredAddress = await getConnectedAddress();
                if (!restoredAddress) {
                    await forceReconnect();
                    await new Promise(r => setTimeout(r, 1000));
                    restoredAddress = await getConnectedAddress();
                }
                if (restoredAddress) {
                    const normalized = restoredAddress.toLowerCase();
                    if (normalized === transientDisconnect.wallet) {
                        console.log('[MobileAuth] Confirmed same wallet after handoff; preserving session');
                    }
                    else {
                        console.log('[MobileAuth] Wallet changed during handoff; replacing session scope');
                    }
                    transitionConnectedAccount(restoredAddress);
                }
                else {
                    console.log('[MobileAuth] AppKit disconnect confirmed after handoff');
                    handleAccountChange({ isConnected: false, confirmed: true });
                    return;
                }
            }
            const bgTimestamp = backgroundTimestampRef.current || Number(localStorage.getItem('mobile_background_at') || '0');
            if (bgTimestamp > 0) {
                const elapsed = Date.now() - bgTimestamp;
                backgroundTimestampRef.current = null;
                localStorage.removeItem('mobile_background_at');
                if (elapsed >= SESSION_BACKGROUND_TIMEOUT_MS) {
                    console.log('[MobileAuth] App was in background for', Math.round(elapsed / 1000), 's (limit:', SESSION_BACKGROUND_TIMEOUT_MS / 1000, 's) — soft disconnect (keep MPC data)');
                    await softDisconnect();
                    return;
                }
                else {
                    console.log('[MobileAuth] App returned from background after', Math.round(elapsed / 1000), 's — session kept alive');
                }
            }
            await new Promise(r => setTimeout(r, 500));
            const pending = sessionStorage.getItem('mobile_connect_pending');
            const isReturningFromWallet = pending && (Date.now() - parseInt(pending, 10)) < 120000;
            if (isReturningFromWallet && !jwt && !isAuthenticatingRef.current && !resumeScheduledRef.current) {
                const connectionOperation = connectionOperationGuardRef.current.begin();
                const isStaleConnectionOperation = () => !connectionOperationGuardRef.current.isCurrent(connectionOperation);
                const immediateAddr = await getConnectedAddress();
                if (isStaleConnectionOperation())
                    return;
                if (immediateAddr) {
                    console.log('[MobileAuth] Visibility: session already live! Address:', immediateAddr);
                    reconnectInFlightRef.current = false;
                    const restoredAddress = transitionConnectedAccount(immediateAddr);
                    sessionStorage.setItem('dwdata_wallet_connected', 'true');
                    sessionStorage.removeItem('mobile_connect_pending');
                    setIsReconnecting(false);
                    reconnectInFlightRef.current = false;
                    const pendingAuth = loadAuthInProgress();
                    if (pendingAuth && pendingAuth.walletAddress === restoredAddress && !resumeScheduledRef.current) {
                        console.log('[MobileAuth] Interrupted auth restored; waiting for explicit Continue');
                        explicitOnboardingWalletRef.current = null;
                        setInterruptedOnboarding(true);
                        authTriggeredForAddress.current = restoredAddress;
                        setRequiresExplicitConfirmation(true);
                        setOnboardingStatus('error');
                        setOnboardingError('Wallet setup was interrupted. Tap Continue to resume.');
                    }
                    return;
                }
                if (reconnectInFlightRef.current) {
                    console.log('[MobileAuth] Visibility handler: reconnect already in flight, skipping');
                    return;
                }
                reconnectInFlightRef.current = true;
                const isAndroid = /android/i.test(navigator.userAgent);
                const maxPolls = isAndroid ? 20 : 8;
                const pollDelay = isAndroid ? 2000 : 2000;
                console.log('[MobileAuth] Page became visible, returning from wallet — polling for session...', isAndroid ? '(Android,' + maxPolls + ' polls)' : '(iOS/other)');
                setIsReconnecting(true);
                try {
                    await forceReconnect();
                    if (isStaleConnectionOperation())
                        return;
                    await new Promise(r => setTimeout(r, isAndroid ? 2000 : 1000));
                    if (isStaleConnectionOperation())
                        return;
                    for (let i = 0; i < maxPolls; i++) {
                        const addr = await getConnectedAddress();
                        if (isStaleConnectionOperation())
                            return;
                        if (addr) {
                            console.log('[MobileAuth] Connection detected after return! Address:', addr, 'poll:', i);
                            reconnectInFlightRef.current = false;
                            const restoredAddress = transitionConnectedAccount(addr);
                            sessionStorage.setItem('dwdata_wallet_connected', 'true');
                            sessionStorage.removeItem('mobile_connect_pending');
                            setIsReconnecting(false);
                            const pendingAuth = loadAuthInProgress();
                            if (pendingAuth && pendingAuth.walletAddress === restoredAddress && !resumeScheduledRef.current) {
                                console.log('[MobileAuth] Interrupted auth returned; waiting for explicit Continue');
                                explicitOnboardingWalletRef.current = null;
                                setInterruptedOnboarding(true);
                                authTriggeredForAddress.current = restoredAddress;
                                setRequiresExplicitConfirmation(true);
                                setOnboardingStatus('error');
                                setOnboardingError('Wallet setup was interrupted. Tap Continue to resume.');
                            }
                            return;
                        }
                        if (i === Math.floor(maxPolls / 3) || i === Math.floor(2 * maxPolls / 3)) {
                            console.log('[MobileAuth] Forcing reconnect retry at poll', i);
                            await forceReconnect();
                            if (isStaleConnectionOperation())
                                return;
                            await new Promise(r => setTimeout(r, 1000));
                            if (isStaleConnectionOperation())
                                return;
                        }
                        await new Promise(r => setTimeout(r, pollDelay));
                        if (isStaleConnectionOperation())
                            return;
                    }
                    console.log('[MobileAuth] Connection not established after', maxPolls, 'polls — ending wallet handoff');
                    endConnectHandoff('Wallet connection timed out. Please try again.');
                }
                finally {
                    if (!isStaleConnectionOperation())
                        reconnectInFlightRef.current = false;
                }
            }
            if (!document.hidden && isConnected && address && !jwt && !isAuthenticatingRef.current && !resumeScheduledRef.current && !isReconnecting) {
                const pendingAuth = loadAuthInProgress();
                if (pendingAuth && pendingAuth.walletAddress === address.toLowerCase()) {
                    console.log('[MobileAuth] Page became visible with pending auth; waiting for explicit Continue');
                    explicitOnboardingWalletRef.current = null;
                    setInterruptedOnboarding(true);
                    authTriggeredForAddress.current = address;
                    setRequiresExplicitConfirmation(true);
                    setOnboardingStatus('error');
                    setOnboardingError('Wallet setup was interrupted. Tap Continue to resume.');
                }
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, [isConnected, address, jwt, isReconnecting, softDisconnect, clearAllWcStorage, navigate, resetAllAuthState, transitionConnectedAccount, endConnectHandoff]);
    const authAttemptTimestamps = useRef([]);
    useEffect(() => {
        if (!sessionStorageChecked)
            return;
        if (!isConnected
            || !address
            || whitelistStatus !== 'authorized'
            || (jwt && backendAuthStatus === 'authorized'))
            return;
        if (isAuthenticatingRef.current)
            return;
        const hasPendingAuth = !!loadAuthInProgress();
        if (onboardingStatus !== 'idle' && !hasPendingAuth)
            return;
        if (authTriggeredForAddress.current === address)
            return;
        const normalizedAddress = address.toLowerCase();
        const hasExactWalletCredential = loadWalletAddress()?.toLowerCase() === normalizedAddress
            && (!!loadRefreshToken() || !!loadJwt());
        if (!hasExactWalletCredential && explicitOnboardingWalletRef.current !== normalizedAddress) {
            // A passive page/app restore is never allowed to enter the wallet-signing
            // fallback. Missing credentials require a fresh, explicit user action.
            // This also prevents a killed Android PWA from replaying an interrupted
            // multi-signature onboarding flow after refresh.
            authTriggeredForAddress.current = address;
            setInterruptedOnboarding(hasPendingAuth);
            setRequiresExplicitConfirmation(true);
            setBackendAuthStatus('idle');
            setOnboardingStatus('error');
            setOnboardingError(hasPendingAuth
                ? 'Wallet setup was interrupted. Tap Continue to resume.'
                : 'Your saved session could not be restored. Tap Continue to sign in again.');
            return;
        }
        const now = Date.now();
        const recentAttempts = authAttemptTimestamps.current.filter(t => now - t < 30000);
        authAttemptTimestamps.current = recentAttempts;
        if (recentAttempts.length >= 3) {
            console.warn('[MobileAuth] Auth loop detected — 3+ attempts in 30s, stopping automatic auth. Manual login required.');
            return;
        }
        authAttemptTimestamps.current.push(now);
        console.log('[MobileAuth] Wallet connected, waiting for relay to stabilize before auth...');
        authTriggeredForAddress.current = address;
        const timer = setTimeout(() => {
            console.log('[MobileAuth] Starting auth after stabilization delay...');
            runAuthProcess();
        }, 3000);
        return () => clearTimeout(timer);
    }, [
        isConnected,
        address,
        jwt,
        backendAuthStatus,
        whitelistStatus,
        onboardingStatus,
        sessionStorageChecked,
        runAuthProcess,
    ]);
    const startOnboarding = useCallback(async () => {
        console.log('[MobileAuth] Manual startOnboarding called');
        if (backendAuthStatus === 'denied'
            && onboardingStatus === 'error'
            && !requiresExplicitConfirmation
            && whitelistStatus === 'authorized') {
            console.log('[MobileAuth] Backend denied this wallet; reconnect or account change is required');
            return;
        }
        clearAuthInProgress();
        clearPreSignedCache();
        setOnboardingStatus('idle');
        setOnboardingError(null);
        authTriggeredForAddress.current = null;
        isAuthenticatingRef.current = false;
        // Retrying an outage is not consent to replace saved credentials with a
        // signed login. In particular, access-only sessions must revalidate silently.
        const retryingSavedSession = backendAuthStatus === 'unavailable'
            && !!address
            && loadWalletAddress()?.toLowerCase() === address.toLowerCase()
            && !!(loadRefreshToken() || loadJwt());
        explicitOnboardingWalletRef.current = retryingSavedSession
            ? null
            : address?.toLowerCase() || '__pending_connect__';
        setRequiresExplicitConfirmation(false);
        if (!isConnected || !address) {
            console.log('[MobileAuth] Wallet not connected, opening connect modal...');
            connect();
            return;
        }
        if (whitelistStatus === 'denied') {
            setOnboardingError('This wallet is not whitelisted.');
            setOnboardingStatus('error');
            return;
        }
        if (whitelistStatus === 'unavailable' || whitelistStatus === 'checking') {
            setOnboardingError('Whitelist verification is temporarily unavailable. Please retry.');
            setOnboardingStatus('error');
            retryWhitelistCheck();
            return;
        }
        if (backendAuthStatus === 'unavailable') {
            console.log('[MobileAuth] Retrying temporarily unavailable silent refresh');
            runAuthProcess();
            return;
        }
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
        setOnboardingStatus('connecting');
        console.log('[MobileAuth] Retry: refreshing WC session before re-auth...');
        const connectionOperation = connectionOperationGuardRef.current.current();
        const isStaleConnectionOperation = () => !connectionOperationGuardRef.current.isCurrent(connectionOperation);
        try {
            const restored = await forceReconnect();
            if (isStaleConnectionOperation())
                return;
            if (!restored) {
                console.log('[MobileAuth] Retry: forceReconnect failed, checking health...');
                const { healthy } = await testProviderHealth();
                if (isStaleConnectionOperation())
                    return;
                if (!healthy) {
                    setOnboardingError('Wallet connection lost. Please disconnect and reconnect your wallet.');
                    setOnboardingStatus('error');
                    return;
                }
            }
        }
        catch (e) {
            console.warn('[MobileAuth] Retry: reconnect error, proceeding anyway:', getAuthDiagnostic(e));
        }
        const settleDelay = isIOS ? 3000 : 1500;
        console.log('[MobileAuth] Retry: waiting', settleDelay, 'ms for WC relay to settle...');
        await new Promise(r => setTimeout(r, settleDelay));
        if (isStaleConnectionOperation())
            return;
        runAuthProcess();
    }, [
        runAuthProcess,
        isConnected,
        address,
        connect,
        backendAuthStatus,
        onboardingStatus,
        requiresExplicitConfirmation,
        whitelistStatus,
        retryWhitelistCheck,
    ]);
    const resumeOnboarding = useCallback(() => {
        startOnboarding();
    }, [startOnboarding]);
    const continueSignature = useCallback(() => {
        console.log('[MobileAuth] continueSignature – no-op for WalletConnect');
    }, []);
    const value = {
        address,
        walletProvider,
        isConnected,
        isConnecting,
        isAuthenticated,
        isInitializing,
        isWaitingForWallet: isConnecting || (isAuthenticatingRef.current && signatureCount > 0),
        connect,
        cancelConnect,
        disconnect,
        error,
        isConfigured: !!projectId,
        jwt,
        connector: undefined,
        onboardingStatus,
        onboardingError,
        startOnboarding,
        resumeOnboarding,
        hasInterruptedOnboarding: interruptedOnboarding,
        signatureCount,
        waitingForSignatures,
        biometricState,
        retryBiometric,
        needsManualSign,
        continueSignature,
        isReconnecting,
        walletSessionVersion,
        backendAuthStatus,
        authoritativeBackendDenial: backendAuthStatus === 'denied'
            && isConnected
            && authoritativeDenial?.walletAddress === address?.toLowerCase()
            && authoritativeDenial?.operation === connectionOperationGuardRef.current.current(),
        requiresExplicitConfirmation,
        whitelistStatus,
        whitelistUnavailableReason,
        retryWhitelistCheck,
    };
    return (_jsx(MobileWalletContext.Provider, { value: value, children: children }));
}
export default MobileWalletProvider;
